/**
 * OPARA v7.1 - Language Context
 * Zero-Root Gateway Architecture
 * 
 * - Root (/) ONLY shows Gateway
 * - All content under /en/, /tr/, /ar/
 * - Auto-redirect based on stored preference
 */

import React, { createContext, useContext, useState, useLayoutEffect } from 'react';
import { 
  SUPPORTED_LANGUAGES, 
  DEFAULT_LANGUAGE, 
  getStoredLanguage, 
  setStoredLanguage,
  hasLanguagePreference,
  getLanguageConfig 
} from '../config/languages';

const LanguageContext = createContext(null);

// Get initial values synchronously (runs once at module load)
const computeInitialValues = () => {
  if (typeof window === 'undefined') {
    return { language: DEFAULT_LANGUAGE, showGateway: true, isReady: true };
  }
  
  const path = window.location.pathname;
  const storedLang = getStoredLanguage();
  
  // Detect language from URL path
  const pathLangMatch = path.match(/^\/(en|tr|ar|fr|de|es)(\/|$)/);
  
  if (pathLangMatch) {
    // User is on a language-specific path
    const langCode = pathLangMatch[1];
    // Sync stored preference with URL
    if (storedLang !== langCode) {
      setStoredLanguage(langCode);
    }
    return { language: langCode, showGateway: false, isReady: true };
  }
  
  if (path === '/' || path === '') {
    // User is on ROOT - this is the Gateway
    // ALWAYS show gateway on root - let user choose language
    return { language: DEFAULT_LANGUAGE, showGateway: true, isReady: true };
  }
  
  // Legacy route - let App.js redirects handle
  return { language: DEFAULT_LANGUAGE, showGateway: false, isReady: true };
};

// Compute once at module initialization
const INITIAL_VALUES = computeInitialValues();

export const LanguageProvider = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState(INITIAL_VALUES.language);
  const [showGateway, setShowGateway] = useState(INITIAL_VALUES.showGateway);
  const [isInitialized] = useState(INITIAL_VALUES.isReady);

  const selectLanguage = (langCode) => {
    if (SUPPORTED_LANGUAGES[langCode]) {
      setCurrentLanguage(langCode);
      setStoredLanguage(langCode);
      setShowGateway(false);
      
      // Navigate to the selected language's home
      window.location.href = `/${langCode}/`;
    }
  };

  const dismissGateway = () => {
    // Default to English if dismissed without selecting
    setCurrentLanguage('en');
    setStoredLanguage('en');
    setShowGateway(false);
    window.location.href = '/en/';
  };

  const resetLanguagePreference = () => {
    localStorage.removeItem('opara_language');
    window.location.href = '/';
  };

  const value = {
    currentLanguage,
    languageConfig: getLanguageConfig(currentLanguage),
    supportedLanguages: SUPPORTED_LANGUAGES,
    showGateway,
    isInitialized,
    selectLanguage,
    dismissGateway,
    resetLanguagePreference,
    hasLanguagePreference: hasLanguagePreference()
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;
